import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ImagePlus } from "lucide-react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { addDoc, collection } from "firebase/firestore";
import { format } from "date-fns";
import { db, storage } from "../../../lib/firebase";
import AutocompleteInput from "./Autocomplete";
import { storageConst } from "../../../utils/constants";
interface FormData {
  eventName: string;
  eventOrganizer: string;
  eventOrganizerPerson: string;
  eventOrganizerContact: string;
  category: string;
  startDate: Date;
  endDate: Date;
  startTime: Date;
  endTime: Date;
  description: string;
  videoLink?: string;
  socialPageLink?: string;

  addressdetail?: string;
  fee: number;
  image: FileList;
  isVirtual: boolean;
  meetingLink?: string;
}

interface AddEventProps {
  open: boolean;
  onClose: () => void;
}

const schema = yup.object({
  eventName: yup.string().required("Event name is required"),
  eventOrganizer: yup.string().required("Event organizer is required"),
  eventOrganizerPerson: yup.string().required("Organizer person is required"),
  eventOrganizerContact: yup.string().required("Contact is required"),
  category: yup.string().required("Category is required"),
  startDate: yup.date().required("Start date is required"),
  endDate: yup
    .date()
    .min(yup.ref("startDate"), "End date must be after start date")
    .required("End date is required"),
  startTime: yup.date().required("Start time is required"),
  endTime: yup
    .date()
    .min(yup.ref("startTime"), "End time must be after start time")
    .required("End time is required"),
  description: yup.string().required("Description is required"),

  videoLink: yup.string().url("Enter a valid URL").optional(),
  socialPageLink: yup.string().url("Enter a valid URL").optional(),
  addressdetail: yup.string().optional(),
  fee: yup
    .number()
    .typeError("Fee must be a number")
    .min(0, "Fee must be at least 0")
    .required("Fee is required"),
  image: yup
    .mixed<FileList>()
    .required("Image is required")
    .test(
      "fileList",
      "Please upload an image",
      (value) => value instanceof FileList && value.length > 0
    ),
  meetingLink: yup.string().url("Enter a valid URL").optional(),
  isVirtual: yup
    .boolean()
    .required("Please specify if the event is virtual or not"),
});

const AddEvent: React.FC<AddEventProps> = ({ open, onClose }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isVirtual, setIsVirtual] = useState(false);
  const [addressData, setAddressData] = useState<{
    address: string;
    lat: number;
    lng: number;
  } | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<FormData>();
  const handleAddressSelect = (address: string, lat: number, lng: number) => {
    setAddressData({ address, lat, lng });
  };
  const saveDate = (date: any) => {
    const startDate = new Date(date);
    const formattedDate = `${startDate.getFullYear()}-${String(
      startDate.getMonth() + 1
    ).padStart(2, "0")}-${String(startDate.getDate()).padStart(
      2,
      "0"
    )} 00:00:00.000`;

    // Save it to Firestore as a string
    return formattedDate;
  };

  const onSubmit = async (data: FormData) => {
    if (!data.image[0]) {
      alert("Please upload an image.");
      return;
    }

    if (!isVirtual && !addressData) {
      alert("Please enter the address");
      return;
    }
    setIsLoading(true);

    try {
      const imageRef = ref(storage, `events/${data.image[0].name}`);
      const snapshot = await uploadBytes(imageRef, data.image[0]);
      console.log("Image Snapshot:", snapshot);
      const imageUrl = await getDownloadURL(snapshot.ref);
      console.log("Image URL:", imageUrl);

      const eventData = {
        youtubelink: data.videoLink,
        uid: localStorage.getItem(storageConst.APPTOKEN),
        eventname: data.eventName,
        organize: data.eventOrganizer,
        organizePerson: data.eventOrganizerPerson,
        contactPerson: data.eventOrganizerContact,
        startdate: saveDate(data.startDate.toISOString()),
        enddate: saveDate(data.endDate.toISOString()),
        createddate: new Date(),
        join: [],
        isVirtual: isVirtual,
        favrioute: [],
        category: data.category,
        eventlink: data.socialPageLink,
        starttime: format(data.startTime, "h:mm aa"),
        endtime: format(data.endTime, "h:mm aa"),
        description: data.description,
        fee: data.fee,
        image: imageUrl,
        publish: true,
        ...(isVirtual
          ? { meetingLink: data.meetingLink }
          : {
              eventaddress: addressData?.address,
              latitude: addressData?.lat,
              longitude: addressData?.lng,
            }),
      };

      console.log("Final Event Data:", eventData);
      await addDoc(collection(db, "products"), eventData);

      alert("Event created successfully");
      onClose();
    } catch (error) {
      console.error("Error adding event:", error);
      alert("Error creating event. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };
  const handleEventTypeChange = (value: string) => {
    setIsVirtual(value === "Virtual");
  };
  return (
    <div
      className={`modal ${
        open ? "block z-50" : "hidden"
      } fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center py-8`}
    >
      <div className="bg-bg rounded-lg shadow-lg w-full max-w-3xl max-h-[600px] flex flex-col">
        <div className="sticky top-0 z-10 px-6 pt-2 shadow-md">
          <div className="text-lg font-medium">Add Event</div>
        </div>
        <form className="space-y-4 p-6 overflow-y-auto flex-grow">
          <div className="border border-dashed rounded-lg p-4 text-center relative">
            <input
              type="file"
              {...register("image", { required: "Image is required" })}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              onChange={handleImageChange}
              accept="image/*"
            />
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Preview"
                className="max-h-40 mx-auto"
              />
            ) : (
              <div className="flex flex-col items-center">
                <ImagePlus className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-400">Upload an image</span>
              </div>
            )}
          </div>
          {errors.image && (
            <p className="text-red-500 text-sm">{errors.image.message}</p>
          )}

          <input
            type="text"
            placeholder="Event Name"
            {...register("eventName", { required: "Event name is required" })}
            className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
          />
          {errors.eventName && (
            <p className="text-red-500 text-sm">{errors.eventName.message}</p>
          )}

          <input
            type="text"
            placeholder="Event Organizer"
            {...register("eventOrganizer", {
              required: "Event organizer is required",
            })}
            className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
          />
          {errors.eventOrganizer && (
            <p className="text-red-500 text-sm">
              {errors.eventOrganizer.message}
            </p>
          )}

          <input
            type="text"
            placeholder="Event Organizer Person"
            {...register("eventOrganizerPerson", {
              required: "Event organizer person is required",
            })}
            className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
          />
          {errors.eventOrganizerPerson && (
            <p className="text-red-500 text-sm">
              {errors.eventOrganizerPerson.message}
            </p>
          )}

          <input
            type="text"
            placeholder="Event Organizer Contact"
            {...register("eventOrganizerContact", {
              required: "Event organizer contact is required",
            })}
            className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
          />
          {errors.eventOrganizerContact && (
            <p className="text-red-500 text-sm">
              {errors.eventOrganizerContact.message}
            </p>
          )}

          <Controller
            name="category"
            control={control}
            rules={{ required: "Category is required" }}
            render={({ field }) => (
              <select
                {...field}
                className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
              >
                <option value="">Select Category</option>
                <option value="Music">Music</option>
                <option value="Education">Education</option>
                <option value="Health">Health</option>
                <option value="Entertainment">Entertainment</option>
              </select>
            )}
          />
          {errors.category && (
            <p className="text-red-500 text-sm">{errors.category.message}</p>
          )}

          <div className="flex space-x-4 w-full">
            <div className="w-1/2">
              <Controller
                name="startDate"
                control={control}
                rules={{ required: "Start date is required" }}
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={(date) => field.onChange(date)}
                    placeholderText="Start Date"
                    className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
                  />
                )}
              />
              {errors.startDate && (
                <p className="text-red-500 text-sm">
                  {errors.startDate.message}
                </p>
              )}
            </div>
            <div className="w-1/2">
              <Controller
                name="endDate"
                control={control}
                rules={{ required: "End date is required" }}
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={(date) => field.onChange(date)}
                    placeholderText="End Date"
                    className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
                  />
                )}
              />
              {errors.endDate && (
                <p className="text-red-500 text-sm">{errors.endDate.message}</p>
              )}
            </div>
          </div>
          <div className="flex space-x-4">
            <div className="w-1/2">
              <Controller
                name="startTime"
                rules={{ required: "Start time is required" }}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={(time) => field.onChange(time)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    placeholderText="Start Time"
                    className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
                  />
                )}
              />
              {errors.startTime && (
                <p className="text-red-500 text-sm">
                  {errors.startTime.message}
                </p>
              )}
            </div>

            <div className="w-1/2">
              <Controller
                name="endTime"
                rules={{ required: "End time is required" }}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={(time) => field.onChange(time)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    placeholderText="End Time"
                    className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
                  />
                )}
              />
              {errors.endTime && (
                <p className="text-red-500 text-sm">{errors.endTime.message}</p>
              )}
            </div>
          </div>

          <textarea
            placeholder="Description"
            {...register("description", {
              required: "Description is required",
            })}
            className="rounded-2xl w-full px-9 focus-visible:outline-none bg-bgCard border pt-7"
          />
          {errors.description && (
            <p className="text-red-500 text-sm">{errors.description.message}</p>
          )}

          <input
            type="url"
            placeholder="Video Link"
            {...register("videoLink")}
            className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
          />
          {errors.videoLink && (
            <p className="text-red-500 text-sm">{errors.videoLink.message}</p>
          )}

          <input
            type="url"
            placeholder="Social Page Link"
            {...register("socialPageLink")}
            className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
          />
          {errors.socialPageLink && (
            <p className="text-red-500 text-sm">
              {errors.socialPageLink.message}
            </p>
          )}

          <div className="mt-4">Event Type:</div>
          <div className="flex items-center">
            <div className="flex space-x-6">
              <div className="flex space-x-2">
                <input
                  type="radio"
                  id="onsite"
                  name="eventType"
                  value="On-site"
                  checked={!isVirtual}
                  onChange={() => handleEventTypeChange("On-site")}
                />
                <div>On-site</div>
              </div>
              <div className="flex space-x-2">
                <input
                  type="radio"
                  id="virtual"
                  name="eventType"
                  value="Virtual"
                  checked={isVirtual}
                  onChange={() => handleEventTypeChange("Virtual")}
                />
                <div>Virtual</div>
              </div>
            </div>
          </div>
          {!isVirtual && (
            <div>
              <AutocompleteInput onAddressSelect={handleAddressSelect} />
            </div>
          )}
          {isVirtual && (
            <input
              type="url"
              placeholder="Meeting Link"
              {...register("meetingLink")}
              className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
            />
          )}
          {isVirtual && errors.meetingLink && (
            <p className="text-red-500 text-sm">{errors.meetingLink.message}</p>
          )}

          <input
            type="number"
            placeholder="Fee"
            {...register("fee", { required: "Fee is required", min: 0 })}
            className="w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
          />
          {errors.fee && (
            <p className="text-red-500 text-sm">{errors.fee.message}</p>
          )}
        </form>

        <div className="sticky bottom-0 z-10 p-6 shadow-md flex justify-end space-x-4">
          <button
            type="button"
            onClick={onClose}
            className="bg-bgCard text-text px-4 py-2 rounded-full"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            className="bg-primary text-white px-4 py-2 hover:bg-primary/50 rounded-full"
          >
            {isLoading ? "Creating..." : "Add Event"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEvent;
